import type { WidgetFabPosition } from "./types.ts";

// Where the surfaces that belong to the launcher – the unread pill, the
// announcement card, the onboarding card, the survey popup – sit on the host
// page. Each one pins next to the launcher's resting place, so a chat-head
// drag carries them along instead of leaving them at the configured corner.

/** The launcher's rectangle in viewport pixels, as `DOMRect` reports it. */
export interface LauncherRect {
	readonly bottom: number;
	readonly left: number;
	readonly right: number;
	readonly top: number;
}

/**
 * Where a surface pins, relative to the viewport. Exactly one of `topPx` /
 * `bottomPx` is set: below the launcher the surface grows downward from
 * `topPx`, above it the surface grows upward from `bottomPx`.
 */
export interface LauncherAnchor {
	readonly bottomPx?: number;
	/** Distance from the anchored side edge. */
	readonly insetPx: number;
	readonly side: WidgetFabPosition;
	readonly topPx?: number;
}

export interface LauncherViewport {
	readonly height: number;
	readonly width: number;
}

// The classic corner: 26px edge inset, 104px = 26 + 64px bubble + 14px gap.
const CORNER_INSET_PX = 26;
const CORNER_BOTTOM_PX = 104;
// Breathing room between the launcher and the surface.
export const LAUNCHER_GAP_PX = 14;
// A tab sits flush on the edge; the surface keeps a readable margin anyway.
const MIN_EDGE_INSET_PX = 16;
// Keeps a surface that fits nowhere clear of the viewport edge.
export const VIEWPORT_MARGIN_PX = 8;

/**
 * The half of the viewport the launcher rests in – the same rule the drag
 * uses to pick the edge it parks on (see `nearestSide` in fab-drag.ts).
 */
export const sideOfLauncher = (
	launcher: LauncherRect,
	viewport: LauncherViewport,
): WidgetFabPosition =>
	(launcher.left + launcher.right) / 2 < viewport.width / 2 ? "left" : "right";

/**
 * Pin a surface above or below the launcher. Above wins when the surface
 * fits there, below when only that side fits, and the roomier side when
 * neither does – then the surface is pinned to that viewport edge so its
 * buttons stay reachable, even if it covers part of the launcher.
 *
 * Without a `surfaceHeight` the roomier side always wins. Without a launcher
 * rectangle (launcher hidden) the surface falls back to the configured corner.
 */
export const computeLauncherAnchor = (
	side: WidgetFabPosition,
	launcher: LauncherRect | undefined,
	viewport: LauncherViewport,
	surfaceHeight?: number,
): LauncherAnchor => {
	if (launcher === undefined) {
		return { bottomPx: CORNER_BOTTOM_PX, insetPx: CORNER_INSET_PX, side };
	}
	const insetPx = Math.max(
		side === "left" ? launcher.left : viewport.width - launcher.right,
		MIN_EDGE_INSET_PX,
	);
	const above: LauncherAnchor = {
		bottomPx: viewport.height - launcher.top + LAUNCHER_GAP_PX,
		insetPx,
		side,
	};
	const below: LauncherAnchor = {
		insetPx,
		side,
		topPx: launcher.bottom + LAUNCHER_GAP_PX,
	};
	const spaceAbove = launcher.top - LAUNCHER_GAP_PX - VIEWPORT_MARGIN_PX;
	const spaceBelow =
		viewport.height - launcher.bottom - LAUNCHER_GAP_PX - VIEWPORT_MARGIN_PX;
	if (surfaceHeight === undefined) {
		return spaceAbove > spaceBelow ? above : below;
	}
	if (surfaceHeight <= spaceAbove) {
		return above;
	}
	if (surfaceHeight <= spaceBelow) {
		return below;
	}
	return spaceAbove > spaceBelow
		? { insetPx, side, topPx: VIEWPORT_MARGIN_PX }
		: { bottomPx: VIEWPORT_MARGIN_PX, insetPx, side };
};
